import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Plus, Edit2, Save, ShoppingBag, Package, Download, Star,
  ShoppingCart, ExternalLink, Truck, Tag, DollarSign, BarChart3,
  Image, Boxes, Archive, AlertTriangle, CheckCircle2, Clock, MapPin
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_CONFIG = {
  digital_products: {
    label: "Digital Products",
    color: "bg-violet-100 text-violet-700 border-violet-200",
    icon: Download,
    gradient: "from-violet-500 to-purple-600"
  },
  merchandise: {
    label: "Branded Merchandise",
    color: "bg-amber-100 text-amber-700 border-amber-200",
    icon: Package,
    gradient: "from-amber-500 to-orange-600"
  },
  business_kits: {
    label: "Business Tool Kits",
    color: "bg-royal-blue-100 text-royal-blue-700 border-royal-blue-200",
    icon: Boxes,
    gradient: "from-royal-blue-500 to-blue-700"
  }
};

export const Store = ({ contacts, onRefresh }) => {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [storeStats, setStoreStats] = useState(null);
  const [activeTab, setActiveTab] = useState("catalog");
  const [activeCategory, setActiveCategory] = useState("all");
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [orderDetailDialog, setOrderDetailDialog] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [shipDialogOrder, setShipDialogOrder] = useState(null);
  const [shipForm, setShipForm] = useState({ tracking_number: "", tracking_url: "" });
  const [productForm, setProductForm] = useState({
    name: "", description: "", price: "", compare_price: "", category: "merchandise",
    product_type: "physical", sku: "", inventory_count: "0", image_url: "", image_placeholder: "",
    tags: "", is_active: true, is_featured: false
  });
  const [imageUploading, setImageUploading] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    quantity: 1, contact_id: "none", customer_email: "", customer_name: ""
  });

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

  const fetchStoreData = useCallback(async () => {
    try {
      const [productsRes, ordersRes, statsRes, lowStockRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/orders`),
        axios.get(`${API}/store/stats`),
        axios.get(`${API}/store/low-stock?threshold=10`)
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setStoreStats(statsRes.data);
      setLowStockProducts(lowStockRes.data);
    } catch (error) {
      console.error("Error fetching store data:", error);
    }
  }, []);

  useEffect(() => { fetchStoreData(); }, [fetchStoreData]);

  const filteredProducts = activeCategory === "all"
    ? products
    : products.filter(p => p.category === activeCategory);

  const handleOpenProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name || "",
        description: product.description || "",
        price: product.price?.toString() || "",
        compare_price: product.compare_price?.toString() || "",
        category: product.category || "merchandise",
        product_type: product.product_type || "physical",
        sku: product.sku || "",
        inventory_count: product.inventory_count?.toString() || "0",
        image_url: product.image_url || "",
        image_placeholder: product.image_placeholder || "",
        tags: (product.tags || []).join(", "),
        is_active: product.is_active !== false,
        is_featured: product.is_featured || false
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: "", description: "", price: "", compare_price: "", category: "merchandise",
        product_type: "physical", sku: "", inventory_count: "0", image_url: "", image_placeholder: "",
        tags: "", is_active: true, is_featured: false
      });
    }
    setProductDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    try {
      const payload = {
        ...productForm,
        price: parseFloat(productForm.price) || 0,
        compare_price: productForm.compare_price ? parseFloat(productForm.compare_price) : null,
        inventory_count: parseInt(productForm.inventory_count) || 0,
        tags: productForm.tags.split(",").map(t => t.trim()).filter(t => t)
      };
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload);
      } else {
        await axios.post(`${API}/products`, payload);
      }
      setProductDialogOpen(false);
      fetchStoreData();
    } catch (error) {
      console.error("Error saving product:", error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("File too large. Max 5MB."); return; }
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/products/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setProductForm(prev => ({ ...prev, image_url: res.data.url }));
    } catch (error) {
      console.error("Image upload error:", error);
      alert("Failed to upload image");
    } finally {
      setImageUploading(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedProduct) return;
    try {
      const payload = {
        product_id: selectedProduct.id,
        quantity: checkoutForm.quantity,
        contact_id: checkoutForm.contact_id !== "none" ? checkoutForm.contact_id : null,
        customer_email: checkoutForm.customer_email,
        customer_name: checkoutForm.customer_name
      };
      const res = await axios.post(`${API}/store/checkout`, payload);
      if (res.data.checkout_url) {
        window.open(res.data.checkout_url, '_blank');
      }
      setCheckoutDialogOpen(false);
      fetchStoreData();
    } catch (error) {
      console.error("Checkout error:", error);
      alert(error.response?.data?.detail || "Checkout failed");
    }
  };

  const openCheckout = (product) => {
    setSelectedProduct(product);
    setCheckoutForm({ quantity: 1, contact_id: "none", customer_email: "", customer_name: "" });
    setCheckoutDialogOpen(true);
  };

  const handleUpdateFulfillment = async (orderId, status, trackingData = {}) => {
    try {
      await axios.put(`${API}/orders/${orderId}/fulfillment`, {
        fulfillment_status: status,
        ...trackingData
      });
      fetchStoreData();
      setShipDialogOrder(null);
    } catch (error) {
      console.error("Fulfillment update error:", error);
    }
  };

  const openShipDialog = (order) => {
    setShipDialogOrder(order);
    setShipForm({ tracking_number: order.tracking_number || "", tracking_url: order.tracking_url || "" });
  };

  const handleShipOrder = () => {
    if (!shipDialogOrder) return;
    handleUpdateFulfillment(shipDialogOrder.id, "shipped", shipForm);
  };

  const ProductCard = ({ product }) => {
    const catConfig = CATEGORY_CONFIG[product.category] || CATEGORY_CONFIG.merchandise;
    const CatIcon = catConfig.icon;
    const discount = product.compare_price ? Math.round((1 - product.price / product.compare_price) * 100) : 0;

    return (
      <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-slate-200 hover:border-slate-300" data-testid={`product-card-${product.id}`}>
        <div className={`h-48 ${product.image_url ? '' : `bg-gradient-to-br ${catConfig.gradient}`} relative flex items-center justify-center overflow-hidden`}>
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <>
              <div className="text-white/20 absolute inset-0 flex items-center justify-center">
                <CatIcon size={80} strokeWidth={1} />
              </div>
              <div className="relative z-10 text-center px-4">
                <Image size={32} className="text-white/70 mx-auto mb-2" />
                <p className="text-white/80 text-xs font-medium leading-tight">{product.image_placeholder || "Product Image"}</p>
              </div>
            </>
          )}
          {product.is_featured && (
            <Badge className="absolute top-3 left-3 bg-yellow-400 text-yellow-900 shadow-lg">
              <Star size={12} className="mr-1" /> Featured
            </Badge>
          )}
          {discount > 0 && (
            <Badge className="absolute top-3 right-3 bg-red-500 text-white shadow-lg">
              -{discount}% OFF
            </Badge>
          )}
          {product.product_type === "digital" && (
            <Badge className="absolute bottom-3 left-3 bg-white/20 text-white backdrop-blur-sm">
              <Download size={12} className="mr-1" /> Digital
            </Badge>
          )}
        </div>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-2">
            <Badge variant="outline" className={`text-xs ${catConfig.color}`}>
              {catConfig.label}
            </Badge>
            {product.product_type === "physical" && (
              <span className="text-xs text-slate-500">{product.inventory_count} in stock</span>
            )}
          </div>
          <h3 className="font-cabinet font-bold text-slate-900 mb-1 group-hover:text-royal-blue-700 transition-colors">{product.name}</h3>
          <p className="text-sm text-slate-500 line-clamp-2 mb-4 min-h-[40px]">{product.description}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <span className="font-cabinet text-xl font-bold text-slate-900">{formatCurrency(product.price)}</span>
              {product.compare_price && (
                <span className="text-sm text-slate-400 line-through">{formatCurrency(product.compare_price)}</span>
              )}
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => handleOpenProductDialog(product)} data-testid={`edit-product-${product.id}`}>
                <Edit2 size={14} />
              </Button>
              <Button size="sm" onClick={() => openCheckout(product)} className="bg-royal-blue-700 hover:bg-royal-blue-800" data-testid={`buy-product-${product.id}`}>
                <ShoppingCart size={14} className="mr-1" /> Buy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="store-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-3xl font-bold text-slate-900">Merchandise & Digital Store</h2>
          <p className="text-slate-500 font-manrope">Products, digital assets & business kits</p>
        </div>
        <Button onClick={() => handleOpenProductDialog()} className="bg-royal-blue-700 hover:bg-royal-blue-800" data-testid="add-product-btn">
          <Plus size={18} className="mr-2" /> Add Product
        </Button>
      </div>

      {storeStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-royal-blue-100"><ShoppingBag size={18} className="text-royal-blue-700" /></div>
                <div>
                  <p className="text-xs text-slate-500">Products</p>
                  <p className="font-cabinet text-lg font-bold">{storeStats.total_products}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100"><DollarSign size={18} className="text-green-700" /></div>
                <div>
                  <p className="text-xs text-slate-500">Revenue</p>
                  <p className="font-cabinet text-lg font-bold text-green-700">{formatCurrency(storeStats.total_revenue)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100"><ShoppingCart size={18} className="text-amber-700" /></div>
                <div>
                  <p className="text-xs text-slate-500">Total Orders</p>
                  <p className="font-cabinet text-lg font-bold">{storeStats.total_orders}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-100"><Download size={18} className="text-violet-700" /></div>
                <div>
                  <p className="text-xs text-slate-500">Digital Rev.</p>
                  <p className="font-cabinet text-lg font-bold">{formatCurrency(storeStats.digital_revenue)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100"><Truck size={18} className="text-red-700" /></div>
                <div>
                  <p className="text-xs text-slate-500">Unfulfilled</p>
                  <p className="font-cabinet text-lg font-bold text-red-600">{storeStats.unfulfilled_orders}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="catalog" data-testid="tab-catalog">Product Catalog</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="fulfillment" data-testid="tab-fulfillment">
            Fulfillment {orders.filter(o => o.status === 'paid' && o.fulfillment_status === 'unfulfilled').length > 0 && (
              <Badge className="ml-1.5 bg-red-500 text-white text-[10px] px-1.5 py-0">{orders.filter(o => o.status === 'paid' && o.fulfillment_status === 'unfulfilled').length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">
            Inventory {lowStockProducts.length > 0 && (
              <Badge className="ml-1.5 bg-amber-500 text-white text-[10px] px-1.5 py-0">{lowStockProducts.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="mt-4 space-y-4">
          <div className="flex gap-2">
            {[{ id: "all", label: "All Products" }, ...Object.entries(CATEGORY_CONFIG).map(([id, c]) => ({ id, label: c.label }))].map(cat => (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveCategory(cat.id)}
                className={activeCategory === cat.id ? "bg-royal-blue-700" : ""}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </Button>
            ))}
          </div>

          {filteredProducts.length === 0 ? (
            <Card className="text-center py-16">
              <CardContent>
                <ShoppingBag size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 font-medium">No products in this category</p>
                <p className="text-sm text-slate-400 mt-1">Add your first product to get started</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium">Order #</th>
                    <th className="text-left p-4 font-medium">Product</th>
                    <th className="text-left p-4 font-medium">Customer</th>
                    <th className="text-left p-4 font-medium">Qty</th>
                    <th className="text-left p-4 font-medium">Total</th>
                    <th className="text-left p-4 font-medium">Payment</th>
                    <th className="text-left p-4 font-medium">Fulfillment</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.id} className="border-b hover:bg-slate-50" data-testid={`order-row-${order.id}`}>
                      <td className="p-4">
                        <span className="font-mono text-sm font-medium">{order.order_number}</span>
                      </td>
                      <td className="p-4">
                        <p className="font-medium text-sm">{order.product_name}</p>
                        <Badge variant="outline" className="text-xs">{order.product_type}</Badge>
                      </td>
                      <td className="p-4">
                        <p className="text-sm">{order.customer_name || '-'}</p>
                        <p className="text-xs text-slate-500">{order.customer_email || '-'}</p>
                      </td>
                      <td className="p-4">{order.quantity}</td>
                      <td className="p-4 font-semibold">{formatCurrency(order.total)}</td>
                      <td className="p-4">
                        <Badge className={
                          order.status === 'paid' ? 'bg-green-100 text-green-600' :
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                          'bg-slate-100 text-slate-600'
                        }>{order.status}</Badge>
                      </td>
                      <td className="p-4">
                        {order.fulfillment_status !== 'not_applicable' ? (
                          <Badge className={
                            order.fulfillment_status === 'delivered' ? 'bg-green-100 text-green-600' :
                            order.fulfillment_status === 'shipped' ? 'bg-blue-100 text-blue-600' :
                            'bg-orange-100 text-orange-600'
                          }>{order.fulfillment_status}</Badge>
                        ) : <span className="text-slate-400 text-sm">N/A</span>}
                      </td>
                      <td className="p-4 text-right">
                        {order.status === 'paid' && order.fulfillment_status === 'unfulfilled' && (
                          <Button size="sm" variant="outline" onClick={() => openShipDialog(order)} className="text-xs" data-testid={`ship-order-${order.id}`}>
                            <Truck size={14} className="mr-1" /> Ship
                          </Button>
                        )}
                        {order.fulfillment_status === 'shipped' && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateFulfillment(order.id, 'delivered')} className="text-xs" data-testid={`deliver-order-${order.id}`}>
                            <CheckCircle2 size={14} className="mr-1" /> Delivered
                          </Button>
                        )}
                        {order.payment_url && order.status === 'pending' && (
                          <Button size="sm" variant="ghost" onClick={() => window.open(order.payment_url, '_blank')}>
                            <ExternalLink size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orders.length === 0 && (
                <div className="text-center py-12">
                  <Archive size={40} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No orders yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fulfillment Tab */}
        <TabsContent value="fulfillment" className="mt-4 space-y-4">
          {orders.filter(o => o.status === 'paid' && o.fulfillment_status === 'unfulfilled').length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3" data-testid="unfulfilled-alert">
              <AlertTriangle size={20} className="text-red-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">
                  {orders.filter(o => o.status === 'paid' && o.fulfillment_status === 'unfulfilled').length} order(s) awaiting fulfillment
                </p>
                <p className="text-xs text-red-600">Paid orders that need to be shipped</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100"><Clock size={18} className="text-orange-700" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Unfulfilled</p>
                    <p className="font-cabinet text-2xl font-bold text-orange-700">{orders.filter(o => o.status === 'paid' && o.fulfillment_status === 'unfulfilled').length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100"><Truck size={18} className="text-blue-700" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Shipped</p>
                    <p className="font-cabinet text-2xl font-bold text-blue-700">{orders.filter(o => o.fulfillment_status === 'shipped').length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100"><CheckCircle2 size={18} className="text-green-700" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Delivered</p>
                    <p className="font-cabinet text-2xl font-bold text-green-700">{orders.filter(o => o.fulfillment_status === 'delivered').length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-cabinet">Physical Orders Requiring Action</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-sm">Order</th>
                    <th className="text-left p-4 font-medium text-sm">Product</th>
                    <th className="text-left p-4 font-medium text-sm">Customer</th>
                    <th className="text-left p-4 font-medium text-sm">Total</th>
                    <th className="text-left p-4 font-medium text-sm">Status</th>
                    <th className="text-left p-4 font-medium text-sm">Tracking</th>
                    <th className="text-right p-4 font-medium text-sm">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.filter(o => o.product_type === 'physical' && o.status === 'paid' && o.fulfillment_status !== 'not_applicable').map(order => (
                    <tr key={order.id} className="border-b hover:bg-slate-50" data-testid={`fulfillment-row-${order.id}`}>
                      <td className="p-4"><span className="font-mono text-sm font-medium">{order.order_number}</span></td>
                      <td className="p-4">
                        <p className="text-sm font-medium">{order.product_name}</p>
                        <p className="text-xs text-slate-500">Qty: {order.quantity}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm">{order.customer_name || '-'}</p>
                        <p className="text-xs text-slate-500">{order.customer_email || '-'}</p>
                      </td>
                      <td className="p-4 font-semibold">{formatCurrency(order.total)}</td>
                      <td className="p-4">
                        <Badge className={
                          order.fulfillment_status === 'delivered' ? 'bg-green-100 text-green-700' :
                          order.fulfillment_status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                          'bg-orange-100 text-orange-700'
                        }>{order.fulfillment_status}</Badge>
                      </td>
                      <td className="p-4">
                        {order.tracking_number ? (
                          <div>
                            <p className="text-xs font-mono">{order.tracking_number}</p>
                            {order.tracking_url && <a href={order.tracking_url} target="_blank" rel="noreferrer" className="text-xs text-royal-blue-600 hover:underline">Track</a>}
                          </div>
                        ) : <span className="text-xs text-slate-400">No tracking</span>}
                      </td>
                      <td className="p-4 text-right">
                        {order.fulfillment_status === 'unfulfilled' && (
                          <Button size="sm" onClick={() => openShipDialog(order)} className="bg-blue-600 hover:bg-blue-700 text-xs" data-testid={`fulfill-ship-${order.id}`}>
                            <Truck size={14} className="mr-1" /> Ship Order
                          </Button>
                        )}
                        {order.fulfillment_status === 'shipped' && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateFulfillment(order.id, 'delivered')} className="text-xs border-green-300 text-green-700 hover:bg-green-50" data-testid={`fulfill-deliver-${order.id}`}>
                            <CheckCircle2 size={14} className="mr-1" /> Mark Delivered
                          </Button>
                        )}
                        {order.fulfillment_status === 'delivered' && (
                          <span className="text-xs text-green-600 flex items-center justify-end gap-1"><CheckCircle2 size={14} /> Complete</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orders.filter(o => o.product_type === 'physical' && o.status === 'paid' && o.fulfillment_status !== 'not_applicable').length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle2 size={40} className="mx-auto text-green-300 mb-3" />
                  <p className="text-slate-500">All orders fulfilled</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="mt-4 space-y-4">
          {lowStockProducts.length > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3" data-testid="low-stock-alert">
              <AlertTriangle size={20} className="text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {lowStockProducts.length} product(s) running low on stock
                </p>
                <p className="text-xs text-amber-600">Products with 10 or fewer units remaining</p>
              </div>
            </div>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-cabinet flex items-center gap-2">
                <Package size={18} className="text-slate-600" /> Physical Product Inventory
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-sm">Product</th>
                    <th className="text-left p-4 font-medium text-sm">SKU</th>
                    <th className="text-left p-4 font-medium text-sm">Category</th>
                    <th className="text-left p-4 font-medium text-sm">Price</th>
                    <th className="text-left p-4 font-medium text-sm">Stock</th>
                    <th className="text-left p-4 font-medium text-sm">Status</th>
                    <th className="text-right p-4 font-medium text-sm">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {products.filter(p => p.product_type === 'physical').map(product => (
                    <tr key={product.id} className="border-b hover:bg-slate-50" data-testid={`inventory-row-${product.id}`}>
                      <td className="p-4">
                        <p className="font-medium text-sm">{product.name}</p>
                      </td>
                      <td className="p-4"><span className="font-mono text-xs text-slate-500">{product.sku || '-'}</span></td>
                      <td className="p-4">
                        <Badge variant="outline" className="text-xs">{CATEGORY_CONFIG[product.category]?.label || product.category}</Badge>
                      </td>
                      <td className="p-4 font-medium">{formatCurrency(product.price)}</td>
                      <td className="p-4">
                        <span className={`font-bold ${product.inventory_count <= 5 ? 'text-red-600' : product.inventory_count <= 10 ? 'text-amber-600' : 'text-green-600'}`}>
                          {product.inventory_count}
                        </span>
                      </td>
                      <td className="p-4">
                        {product.inventory_count === 0 ? (
                          <Badge className="bg-red-100 text-red-700">Out of Stock</Badge>
                        ) : product.inventory_count <= 5 ? (
                          <Badge className="bg-red-100 text-red-700">Critical</Badge>
                        ) : product.inventory_count <= 10 ? (
                          <Badge className="bg-amber-100 text-amber-700">Low Stock</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">In Stock</Badge>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <Button size="sm" variant="outline" onClick={() => handleOpenProductDialog(product)} className="text-xs">
                          <Edit2 size={14} className="mr-1" /> Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {products.filter(p => p.product_type === 'physical').length === 0 && (
                <div className="text-center py-12">
                  <Package size={40} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No physical products</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Product Create/Edit Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
            <DialogDescription>
              {editingProduct ? 'Update product details' : 'Add a new product to the store'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Product Name *</Label>
                <Input data-testid="product-name-input" value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>SKU</Label>
                <Input value={productForm.sku} onChange={e => setProductForm({...productForm, sku: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea rows={3} value={productForm.description} onChange={e => setProductForm({...productForm, description: e.target.value})} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Price ($) *</Label>
                <Input type="number" step="0.01" data-testid="product-price-input" value={productForm.price} onChange={e => setProductForm({...productForm, price: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Compare Price ($)</Label>
                <Input type="number" step="0.01" value={productForm.compare_price} onChange={e => setProductForm({...productForm, compare_price: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Inventory Count</Label>
                <Input type="number" value={productForm.inventory_count} onChange={e => setProductForm({...productForm, inventory_count: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={productForm.category} onValueChange={v => setProductForm({...productForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="digital_products">Digital Products</SelectItem>
                    <SelectItem value="merchandise">Branded Merchandise</SelectItem>
                    <SelectItem value="business_kits">Business Tool Kits</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Product Type</Label>
                <Select value={productForm.product_type} onValueChange={v => setProductForm({...productForm, product_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physical">Physical</SelectItem>
                    <SelectItem value="digital">Digital</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Product Image</Label>
              <div className="flex items-center gap-4">
                {productForm.image_url && (
                  <img src={productForm.image_url} alt="Preview" className="w-20 h-20 rounded-lg object-cover border" />
                )}
                <div className="flex-1">
                  <label className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-100 hover:border-royal-blue-400 transition-colors" data-testid="image-upload-area">
                    <Image size={18} className="text-slate-400" />
                    <span className="text-sm text-slate-500">
                      {imageUploading ? "Uploading..." : productForm.image_url ? "Replace image" : "Click to upload image"}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={imageUploading}
                      data-testid="image-upload-input"
                    />
                  </label>
                  <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP or GIF. Max 5MB.</p>
                </div>
              </div>
              {productForm.image_url && (
                <div className="flex items-center gap-2">
                  <Input value={productForm.image_url} readOnly className="text-xs bg-slate-50" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => setProductForm(prev => ({...prev, image_url: ""}))} className="text-red-500 text-xs flex-shrink-0">Remove</Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Image Fallback Description</Label>
              <Input placeholder="Shown when no image uploaded" value={productForm.image_placeholder} onChange={e => setProductForm({...productForm, image_placeholder: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Tags (comma separated)</Label>
              <Input placeholder="hoodie, apparel, premium..." value={productForm.tags} onChange={e => setProductForm({...productForm, tags: e.target.value})} />
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={productForm.is_active} onChange={e => setProductForm({...productForm, is_active: e.target.checked})} className="rounded" />
                <span className="text-sm">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={productForm.is_featured} onChange={e => setProductForm({...productForm, is_featured: e.target.checked})} className="rounded" />
                <span className="text-sm">Featured</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProduct} className="bg-royal-blue-700 hover:bg-royal-blue-800" data-testid="save-product-btn">
              <Save size={16} className="mr-2" /> Save Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={checkoutDialogOpen} onOpenChange={setCheckoutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purchase: {selectedProduct?.name}</DialogTitle>
            <DialogDescription>Complete your purchase with Stripe Checkout</DialogDescription>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{selectedProduct.name}</p>
                    <p className="text-sm text-slate-500">{selectedProduct.product_type === 'digital' ? 'Digital Download' : 'Physical Product'}</p>
                  </div>
                  <span className="font-cabinet text-xl font-bold">{formatCurrency(selectedProduct.price)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input type="number" min="1" max={selectedProduct.product_type === 'physical' ? selectedProduct.inventory_count : 99} value={checkoutForm.quantity} onChange={e => setCheckoutForm({...checkoutForm, quantity: parseInt(e.target.value) || 1})} />
              </div>
              <div className="space-y-2">
                <Label>Link to CRM Contact (Optional)</Label>
                <Select value={checkoutForm.contact_id} onValueChange={v => setCheckoutForm({...checkoutForm, contact_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select contact..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No linked contact</SelectItem>
                    {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Customer Name</Label>
                  <Input value={checkoutForm.customer_name} onChange={e => setCheckoutForm({...checkoutForm, customer_name: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Customer Email</Label>
                  <Input type="email" value={checkoutForm.customer_email} onChange={e => setCheckoutForm({...checkoutForm, customer_email: e.target.value})} />
                </div>
              </div>
              <Separator />
              <div className="flex justify-between items-center text-lg font-bold">
                <span>Total</span>
                <span className="text-royal-blue-700">{formatCurrency(selectedProduct.price * checkoutForm.quantity)}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCheckout} className="bg-green-600 hover:bg-green-700" data-testid="proceed-checkout-btn">
              <ShoppingCart size={16} className="mr-2" /> Proceed to Stripe Checkout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ship Order Dialog */}
      <Dialog open={!!shipDialogOrder} onOpenChange={() => setShipDialogOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Truck size={20} className="text-blue-600" /> Ship Order</DialogTitle>
            <DialogDescription>
              Add tracking info and mark as shipped. Customer will be emailed automatically.
            </DialogDescription>
          </DialogHeader>
          {shipDialogOrder && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium">{shipDialogOrder.product_name}</p>
                <p className="text-xs text-slate-500">Order {shipDialogOrder.order_number} - Qty: {shipDialogOrder.quantity}</p>
                <p className="text-xs text-slate-500">Customer: {shipDialogOrder.customer_name || shipDialogOrder.customer_email || 'N/A'}</p>
              </div>
              <div className="space-y-2">
                <Label>Tracking Number</Label>
                <Input
                  data-testid="tracking-number-input"
                  value={shipForm.tracking_number}
                  onChange={e => setShipForm({...shipForm, tracking_number: e.target.value})}
                  placeholder="e.g. 1Z999AA10123456784"
                />
              </div>
              <div className="space-y-2">
                <Label>Tracking URL (optional)</Label>
                <Input
                  data-testid="tracking-url-input"
                  value={shipForm.tracking_url}
                  onChange={e => setShipForm({...shipForm, tracking_url: e.target.value})}
                  placeholder="https://tracking.example.com/..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShipDialogOrder(null)}>Cancel</Button>
            <Button onClick={handleShipOrder} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-ship-btn">
              <Truck size={16} className="mr-2" /> Confirm Shipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
